import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  InputNumber,
  message,
  Modal,
  notification,
  Row,
  Select,
  Switch,
} from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { addDoc, collection, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import styled from "styled-components";
import { cloneDeep } from "lodash";
import QuantityControl from "components/shared-components/QuantityControl";
import { DeleteOutlined } from "@ant-design/icons";

// Constants
import { ORDER_STATUS_CREATED } from "../../../../../constants/OrderConstant";
import {
  FIRESTORE_ACQUISITION_DEFAULTS_DOC,
  FIRESTORE_LEADS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
  FIRESTORE_SETTINGS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { CHEFS_FARM, COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { PAYMENT_METHOD_DIRECT_DEBIT } from "../../../../../constants/PaymentConstants";
import { LEAD_STATUS_TRIAL_SENT } from "../../../../../constants/LeadConstant";

// Functions
import { getPickingDate } from "../../../../../functions/deliveryValidation";
import axios from "axios";
import urls from "../../../../../urls";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { useStockData } from "../../../../../data/hooks/firestore/useStock";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import dayjs from "dayjs";

const { Option } = Select;

const AddShipmentModal = ({ visible, onClose, leads, products, selectedDeliveryDate, order, orders }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { email } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [deliveryLabel, setDeliveryLabel] = useState("SCH");
  const [deliveryTimeSlot, setDeliveryTimeSlot] = useState(null);
  const [newOrderProducts, setNewOrderProducts] = useState([]);
  const [newProduct, setNewProduct] = useState("");
  const [productsExceedingStock, setProductsExceedingStock] = useState({});
  const [tempDeliveryDate, setTempDeliveryDate] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const { stockData, stockCarryOverData } = useStockData();
  const [selectedDeliveryDateForStock, setSelectedDeliveryDateForStock] = useState(null);
  const { getCountryDelivery, getCountryDeliveryLabels, getDeliveryLabelLeadDays } = useCountries();

  // Delivery date -> packing dateId (DDMMYYYY), using the lead time
  // configured for the selected lead's country + delivery label.
  const getShipmentPackingDateId = (deliveryDate) => {
    if (!deliveryDate) return null;
    const leadDays = getDeliveryLabelLeadDays(selectedLead?.country || COMPANY_COUNTRY_CODE, deliveryLabel);
    return getPickingDate(deliveryDate, leadDays).format("DDMMYYYY");
  };

  // Normalize timeslot to JSON with consistent key order to prevent display mismatches
  const stringifyTimeSlot = (slot) =>
    JSON.stringify({ start: slot.start, end: slot.end, deliverySurcharge: Number(slot.deliverySurcharge || 0) });

  const [defaultProductsModalOpen, setDefaultProductsModalOpen] = useState(false);
  const [defaultProductsDraft, setDefaultProductsDraft] = useState([]);
  const [newDefaultProduct, setNewDefaultProduct] = useState(null);
  const [defaultProductsLoading, setDefaultProductsLoading] = useState(false);

  // Filter products: default only Chef's Farm, with switch for all products
  const productsToUse = useMemo(() => {
    if (showAllProducts) {
      return products;
    }
    return products?.filter((p) => p.company === CHEFS_FARM && p.productInSeason === true);
  }, [products, showAllProducts]);

  const getAcquisitionDefaultsRef = () =>
    doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_SETTINGS_TABLE,
      FIRESTORE_ACQUISITION_DEFAULTS_DOC,
    );

  const loadDefaultShipmentProducts = async () => {
    try {
      const snap = await getDoc(getAcquisitionDefaultsRef());
      if (!snap.exists()) return [];
      const data = snap.data();
      const parsed = data?.defaultShipmentProducts;
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((item) => item && typeof item.productId === "string" && typeof item.quantity === "number")
        .map((item) => ({ productId: item.productId, quantity: Math.max(1, Math.floor(item.quantity)) }));
    } catch (e) {
      console.error("Failed to load default shipment products from Firestore:", e);
      return [];
    }
  };

  const saveDefaultShipmentProducts = async (items) => {
    try {
      await setDoc(getAcquisitionDefaultsRef(), { defaultShipmentProducts: items }, { merge: true });
    } catch (e) {
      console.error("Failed to save default shipment products to Firestore:", e);
    }
  };

  // Helpers for stock logic
  const isStockCarriedOver = (packingDateId) => stockCarryOverData[packingDateId] === true;

  const getStockInfo = (packingDateId, productId, source = stockData) => {
    return (
      source?.[packingDateId]?.[productId] || {
        currentStock: 0,
        projectedStock: 0,
        requestedStock: 0,
      }
    );
  };

  const getAvailableStock = (stockInfo, carriedOver) => {
    return carriedOver
      ? stockInfo.currentStock || 0
      : (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
  };

  // (Re)set state when modal opens/closes
  useEffect(() => {
    if (visible) {
      resetState();
      form.setFieldsValue({
        deliveryDate: selectedDeliveryDate,
      });
      if (order) {
        const dbLead = leads.find((lead) => lead.leadId === order.leadId);

        // Parse delivery time from order if available
        let timeSlotValue = null;
        if (order.deliveryTime && order.deliveryTime.includes("-")) {
          const [start, end] = order.deliveryTime.split("-");
          const timeSlot = {
            start: start.trim(),
            end: end.trim(),
            deliverySurcharge: order.deliveryTimeCharge || 0,
          };
          timeSlotValue = stringifyTimeSlot(timeSlot);
          setDeliveryTimeSlot(timeSlot);
        }

        form.setFieldsValue({
          leadId: order.leadId,
          deliveryDate: dayjs(order.farmorderDeliveryDate),
          deliveryLabel: order.deliveryLabel,
          deliveryTimeSlot: timeSlotValue,
        });
        setNewOrderProducts(order.userShoppingCart);
        setSelectedLead(dbLead);
        setDeliveryLabel(order.deliveryLabel);
      } else {
        loadDefaultShipmentProducts().then((defaults) => {
          if (defaults.length) {
            const defaultCartItems = defaults
              .map(({ productId, quantity }) => {
                const product = productsToUse?.find((p) => p.productId === productId);
                if (!product) return null;
                return {
                  externalId: product.exactId?.toString() || "",
                  itemCount: quantity,
                  orderable: true,
                  productCategory: product.category?.en || "",
                  productId: product.productId,
                  productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
                  productName: product.productName,
                  productPrice: "0.00",
                  productVat: 0,
                  productBarcode: product.barcode || "",
                };
              })
              .filter(Boolean);
            setNewOrderProducts(defaultCartItems);
          } else {
            setNewOrderProducts([]);
          }
        });
        setProductsExceedingStock({});
      }
    }
  }, [visible, selectedDeliveryDate, order, leads, form, productsToUse]);

  const resetState = () => {
    form.resetFields();
    setSelectedLead(null);
    setDeliveryLabel("SCH");
    setDeliveryTimeSlot(null);
    setNewOrderProducts([]);
    setNewProduct("");
    setProductsExceedingStock({});
    setTempDeliveryDate(null);
    setLoading(false);
    setShowAllProducts(false);
    setSelectedDeliveryDateForStock(null);
    setDefaultProductsModalOpen(false);
    setDefaultProductsDraft([]);
    setNewDefaultProduct(null);
  };

  const validateAndUpdateDate = async (date) => {
    if (!date || !newOrderProducts || newOrderProducts.length === 0) return;

    try {
      const packingDateId = getShipmentPackingDateId(date);
      const newStockData = stockData[packingDateId] || {};
      const stockCarriedOver = isStockCarriedOver(packingDateId);

      const unavailableProducts = [];
      const exceedingProducts = {};

      for (const product of newOrderProducts) {
        let originalQuantity = 0;
        if (order && order.userShoppingCart) {
          const originalProduct = order.userShoppingCart.find((item) => item.productId === product.productId);
          if (originalProduct) {
            originalQuantity = originalProduct.itemCount;
          }
        }

        const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: newStockData });
        const originalPackingDateId = getShipmentPackingDateId(order?.farmorderDeliveryDate);
        const baseAvailable = getAvailableStock(stockInfo, stockCarriedOver);
        const effectiveStock = baseAvailable + (packingDateId === originalPackingDateId ? originalQuantity : 0);

        if (product.itemCount > effectiveStock) {
          unavailableProducts.push({
            name: product.productName,
            ordered: product.itemCount,
            available: effectiveStock,
          });
          exceedingProducts[product.productId] = true;
        }
      }

      if (unavailableProducts.length > 0) {
        Modal.confirm({
          title: t("notifications.stock_issues_for_selected_date"),
          content: (
            <div>
              <p>{t("notifications.insufficient_stock_for_date")}</p>
              <ul style={{ margin: "0 0 20px 20px", padding: 0 }}>
                {unavailableProducts.map((product, index) => (
                  <li key={index}>
                    {product.name}: {t("ordered")} {product.ordered}, {t("available")} {product.available}
                  </li>
                ))}
              </ul>
              <p>{t("proceed_anyway_negative_stock_warning")}</p>
            </div>
          ),
          zIndex: 1100,
          okText: t("proceed_anyway"),
          cancelText: t("cancel"),
          onOk: () => {
            form.setFieldsValue({ deliveryDate: date });
            setTempDeliveryDate(date);
            setProductsExceedingStock(exceedingProducts);
          },
          onCancel: () => {
            form.setFieldsValue({ deliveryDate: tempDeliveryDate });
          },
        });
      } else {
        form.setFieldsValue({ deliveryDate: date });
        setTempDeliveryDate(date);
        setProductsExceedingStock({});
      }
    } catch (error) {
      console.error("Error checking stock for new date:", error);
      message.error(t("notifications.failed_check_stock"));
    }
  };

  const productDropdownOptions = useMemo(() => {
    const deliveryDate = selectedDeliveryDateForStock || form.getFieldValue("deliveryDate");
    if (!deliveryDate) return [];

    const packingDateId = getShipmentPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);

    return [...productsToUse]
      ?.filter((item) => !newOrderProducts.some((orderProduct) => orderProduct.productId === item.productId))
      ?.sort((a, b) => a.productName.localeCompare(b.productName))
      ?.map((product) => {
        const { productId, productName } = product;
        const stockInfo = getStockInfo(packingDateId, productId, stockData);
        const availableStock = getAvailableStock(stockInfo, stockCarriedOver);
        const isInStock = availableStock > 0;

        return {
          productId,
          productName,
          availableStock,
          isInStock,
        };
      });
  }, [productsToUse, newOrderProducts, selectedDeliveryDateForStock, stockData, stockCarryOverData, form]);

  const defaultProductsDropdownOptions = useMemo(() => {
    return [...(productsToUse || [])]
      .sort((a, b) => (a.productName || "").localeCompare(b.productName || ""))
      .map((p) => ({
        productId: p.productId,
        productName: p.productName,
      }));
  }, [productsToUse]);

  const openDefaultProductsModal = async () => {
    setDefaultProductsLoading(true);
    const defaults = await loadDefaultShipmentProducts();
    const draftWithNames = defaults
      .map(({ productId, quantity }) => {
        const product = productsToUse?.find((p) => p.productId === productId);
        return {
          productId,
          quantity,
          productName: product?.productName || "",
        };
      })
      .filter((item) => !!item.productId);

    setDefaultProductsDraft(draftWithNames);
    setNewDefaultProduct(null);
    setDefaultProductsLoading(false);
    setDefaultProductsModalOpen(true);
  };

  const applyDefaultsToCurrentShipment = (defaults) => {
    const cartItems = defaults
      .map(({ productId, quantity }) => {
        const product = productsToUse?.find((p) => p.productId === productId);
        if (!product) return null;
        return {
          externalId: product.exactId?.toString() || "",
          itemCount: quantity,
          orderable: true,
          productCategory: product.category?.en || "",
          productId: product.productId,
          productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
          productName: product.productName,
          productPrice: "0.00",
          productVat: 0,
          productBarcode: product.barcode || "",
        };
      })
      .filter(Boolean);

    setNewOrderProducts(cartItems);
    setProductsExceedingStock({});
  };

  const handleLeadChange = (value) => {
    const lead = leads.find((lead) => lead.leadId === value);
    if (!lead) return;

    const previousLeadId = selectedLead?.leadId;
    const isSwitchingLeads = !!previousLeadId && previousLeadId !== value;

    if (isSwitchingLeads && newOrderProducts.length > 0) {
      Modal.confirm({
        title: t("notifications.confirm_change_lead"),
        content: t("notifications.confirm_change_lead_products_lost"),
        okText: t("confirm"),
        cancelText: t("cancel"),
        onOk: () => {
          setSelectedLead(lead);

          const switchLabelToUse = lead.deliveryLabel || "SCH";
          setDeliveryLabel(switchLabelToUse);

          const switchLeadDeliveryDate = lead.desiredDeliveryDate ? dayjs(lead.desiredDeliveryDate) : null;

          let switchTimeSlotValue = null;
          let switchTimeSlotObject = null;
          if (lead.deliveryTimeSlot) {
            switchTimeSlotObject = lead.deliveryTimeSlot;
            switchTimeSlotValue = stringifyTimeSlot(lead.deliveryTimeSlot);
          } else {
            const switchDeliverySettings = getCountryDelivery(lead.country || COMPANY_COUNTRY_CODE);
            const switchLabelConfig = switchDeliverySettings?.deliveryLabels?.find(
              (dl) => dl.deliveryLabel === switchLabelToUse,
            );
            if (switchLabelConfig?.deliveryTimeSlots?.length > 0) {
              switchTimeSlotObject = switchLabelConfig.deliveryTimeSlots[0];
              switchTimeSlotValue = stringifyTimeSlot(switchTimeSlotObject);
            }
          }

          setDeliveryTimeSlot(switchTimeSlotObject);
          form.setFieldsValue({
            deliveryLabel: switchLabelToUse,
            deliveryTimeSlot: switchTimeSlotValue,
            ...(switchLeadDeliveryDate ? { deliveryDate: switchLeadDeliveryDate } : {}),
          });

          setNewOrderProducts([]);
          setProductsExceedingStock({});
        },
        onCancel: () => {
          form.setFieldsValue({ leadId: previousLeadId });
        },
      });
      return;
    }

    // Initial lead selection: keep any prefilled/default products
    setSelectedLead(lead);

    // Prefill delivery label from lead, fallback to "SCH"
    const labelToUse = lead.deliveryLabel || "SCH";
    setDeliveryLabel(labelToUse);

    // Prefill delivery date from lead if available
    const leadDeliveryDate = lead.desiredDeliveryDate ? dayjs(lead.desiredDeliveryDate) : null;

    // Prefill time slot from lead if available, otherwise auto-set first slot for the label
    let timeSlotValue = null;
    let timeSlotObject = null;
    if (lead.deliveryTimeSlot) {
      timeSlotObject = lead.deliveryTimeSlot;
      timeSlotValue = stringifyTimeSlot(lead.deliveryTimeSlot);
    } else {
      const deliverySettings = getCountryDelivery(lead.country || COMPANY_COUNTRY_CODE);
      const labelConfig = deliverySettings?.deliveryLabels?.find((dl) => dl.deliveryLabel === labelToUse);
      if (labelConfig?.deliveryTimeSlots?.length > 0) {
        timeSlotObject = labelConfig.deliveryTimeSlots[0];
        timeSlotValue = stringifyTimeSlot(timeSlotObject);
      }
    }

    setDeliveryTimeSlot(timeSlotObject);
    form.setFieldsValue({
      deliveryLabel: labelToUse,
      deliveryTimeSlot: timeSlotValue,
      ...(leadDeliveryDate ? { deliveryDate: leadDeliveryDate } : {}),
    });
  };

  const addProductToNewOrder = (product) => {
    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getShipmentPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);
    const stockInfo = getStockInfo(packingDateId, product.productId, stockData);

    const newProduct = {
      externalId: product.exactId?.toString() || "",
      itemCount: 1,
      orderable: true,
      productCategory: product.category?.en || "",
      productId: product.productId,
      productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
      productName: product.productName,
      productPrice: "0.00", // Always 0 for acquisition orders
      productVat: 0,
      productBarcode: product.barcode || "",
    };

    setNewOrderProducts((prevProducts) => [...prevProducts, newProduct]);

    const availableStock = getAvailableStock(stockInfo, stockCarriedOver);
    if (availableStock < 1) {
      setProductsExceedingStock((prev) => ({
        ...prev,
        [product.productId]: true,
      }));
    }
  };

  const increaseQuantity = (product) => {
    const index = newOrderProducts.findIndex((item) => item.productId === product.productId);
    const newProducts = cloneDeep(newOrderProducts);
    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getShipmentPackingDateId(deliveryDate);
    const stockCarriedOver = isStockCarriedOver(packingDateId);
    const stockInfo = getStockInfo(packingDateId, product.productId, stockData);

    if (index !== -1) {
      newProducts[index].itemCount += 1;
      const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

      if (newProducts[index].itemCount > availableStock) {
        setProductsExceedingStock((prev) => ({
          ...prev,
          [product.productId]: true,
        }));
      } else {
        setProductsExceedingStock((prev) => {
          const newState = { ...prev };
          delete newState[product.productId];
          return newState;
        });
      }
    }

    setNewOrderProducts(newProducts);
  };

  const decreaseQuantity = (product) => {
    const index = newOrderProducts.findIndex((item) => item.productId === product.productId);
    const newProducts = cloneDeep(newOrderProducts);

    if (index !== -1) {
      newProducts[index].itemCount -= 1;

      if (productsExceedingStock[product.productId]) {
        const deliveryDate = form.getFieldValue("deliveryDate");
        const packingDateId = getShipmentPackingDateId(deliveryDate);
        const stockCarriedOver = isStockCarriedOver(packingDateId);
        const stockInfo = getStockInfo(packingDateId, product.productId, stockData);
        const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

        if (newProducts[index].itemCount <= availableStock) {
          setProductsExceedingStock((prev) => {
            const newState = { ...prev };
            delete newState[product.productId];
            return newState;
          });
        }
      }

      if (newProducts[index].itemCount === 0) {
        newProducts.splice(index, 1);
        setProductsExceedingStock((prev) => {
          const newState = { ...prev };
          delete newState[product.productId];
          return newState;
        });
      }
    }

    setNewOrderProducts(newProducts);
  };

  const validateStock = () => {
    const deliveryDate = form.getFieldValue("deliveryDate");
    const packingDateId = getShipmentPackingDateId(deliveryDate);
    const latestStockData = stockData[packingDateId] || {};
    const stockCarriedOver = isStockCarriedOver(packingDateId);

    const stockErrors = [];
    for (const product of newOrderProducts) {
      const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: latestStockData });
      const availableStock = getAvailableStock(stockInfo, stockCarriedOver);

      if (product.itemCount > availableStock) {
        stockErrors.push({
          name: product.productName,
          ordered: product.itemCount,
          available: availableStock,
        });
      }
    }

    return stockErrors;
  };

  const showOperationSummary = (results) => {
    const successfulOps = [];
    const failedOps = [];

    Object.entries(results).forEach(([operation, result]) => {
      if (!result.attempted) return;

      if (result.success && !result.error) {
        successfulOps.push(getOperationDisplayName(operation));
      } else if (result.success && result.error) {
        // Partial success
        successfulOps.push(getOperationDisplayName(operation));
      } else {
        failedOps.push({ operation: getOperationDisplayName(operation), error: result.error });
      }
    });

    const hasFailures = failedOps.length > 0;
    const title = hasFailures
      ? t("notifications.shipment_creation_partial")
      : t("notifications.shipment_creation_complete");

    const content = (
      <div>
        {successfulOps.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ color: "#52c41a" }}>{t("successful_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {successfulOps.map((op, index) => (
                <li key={index}>{op}</li>
              ))}
            </ul>
          </div>
        )}

        {failedOps.length > 0 && (
          <div>
            <strong style={{ color: "#ff4d4f" }}>{t("failed_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {failedOps.map(({ operation, error }, index) => (
                <li key={index}>
                  {operation}: {error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );

    notification.destroy();
    notification[hasFailures ? "error" : "success"]({
      message: title,
      description: content,
      duration: 0,
      style: { width: "400px" },
    });
  };

  const getOperationDisplayName = (operation) => {
    const names = {
      firestore: t("shipment_created_database"),
      stock: t("stock_updated"),
      sooCool: t("notifications.soocool_delivery_created"),
    };
    return names[operation] || operation;
  };

  const handleSubmit = (bypassCheck = false) => {
    setLoading(true);

    form
      .validateFields()
      .then(async (values) => {
        try {
          if (newOrderProducts.length === 0) {
            message.error(t("notifications.add_products_first"));
            return;
          }

          const operationResults = {
            firestore: { attempted: true, success: false, error: null },
            stock: { attempted: false, success: false, error: null },
            sooCool: { attempted: false, success: false, error: null },
          };

          // Validate stock unless bypassed
          if (!bypassCheck) {
            const stockErrors = validateStock();
            if (stockErrors.length) {
              Modal.confirm({
                title: t("notifications.stock_validation_failed"),
                content: (
                  <div>
                    <p>{t("notifications.products_exceed_stock")}</p>
                    <ul>
                      {stockErrors.map((error, index) => (
                        <li key={index}>
                          {error.name}: {t("ordered")} {error.ordered}, {t("available")} {error.available}
                        </li>
                      ))}
                    </ul>
                    <p>{t("proceed_anyway_negative_stock_warning")}</p>
                  </div>
                ),
                okText: t("proceed_anyway"),
                cancelText: t("cancel"),
                onOk: () => handleSubmit(true),
                onCancel: () => setLoading(false),
              });
              return;
            }
          }

          const lead = leads.find((lead) => lead.leadId === values.leadId);

          // Get delivery time and charge from selected time slot
          let deliveryTime = "10:00-18:00";
          let deliveryTimeCharge = 0;
          let deliveryTimeSlotObject = null;

          const selectedTimeSlotValue = form.getFieldValue("deliveryTimeSlot");
          if (selectedTimeSlotValue) {
            try {
              const timeSlot = JSON.parse(selectedTimeSlotValue);
              deliveryTime = `${timeSlot.start}-${timeSlot.end}`;
              deliveryTimeCharge = timeSlot.deliverySurcharge || 0;
              deliveryTimeSlotObject = timeSlot;
            } catch (e) {
              console.error("Error parsing time slot:", e);
            }
          }

          const orderObj = {
            type: "acquisition", // Mark as acquisition order
            leadId: lead.leadId,
            // farmorderCustomerId: lead.leadId, // Use leadId as customer identifier
            farmorderCustomerName: lead.companyName,
            customerDiscount: 0,
            farmorderRegion: lead.region || "NL",
            deliveryCostThreshold: 0,
            paymentMethod: PAYMENT_METHOD_DIRECT_DEBIT,
            deliveryTime: deliveryTime,
            deliveryTimeCharge: deliveryTimeCharge,
            deliveryTimeSlot: deliveryTimeSlotObject,
            deliveryNotes: "",
            deliveryLabel: deliveryLabel,
            farmorderComments: "",
            farmorderDeliveryDate: dayjs(values.deliveryDate).format("YYYY-MM-DD"),
            farmorderStatus: ORDER_STATUS_CREATED,
            farmorderUserFirstName: lead.contactFirstName || "",
            farmorderUserLastName: lead.contactLastName || "",
            userLanguage: "en",
            userEmail: lead.email || "",
            farmorderUserId: "",
            deliveryBy: "Chef's Farm",
            farmorderDateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
            userShoppingCart: newOrderProducts,
            orderDateTime: dayjs().unix() * 1000,
            subTotalAmount: 0,
            subTotalWithoutDiscount: 0,
            invoiceAdditionalCosts: 0,
            shippingCost: "0",
            totalAmount: 0,
            tax: 0,
            createdBy: `CMS - ${email}`,
            shippingCostDisabled: true,
          };

          // SAVE TO FIRESTORE
          try {
            const docRef = await addDoc(
              collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE),
              orderObj,
            );

            // Generate shortened farmorderId for acquisition orders (max 6 characters)
            const fullDocId = docRef.id;
            const farmorderId = fullDocId.substring(0, 6).toUpperCase();

            await updateDoc(
              doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, fullDocId),
              { farmorderId: farmorderId, updatedAt: dayjs().unix(), updatedBy: email },
            );

            operationResults.firestore.success = true;

            // UPDATE LEAD STATUS TO TRIAL_SENT
            try {
              await updateDoc(
                doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, lead.leadId),
                {
                  status: LEAD_STATUS_TRIAL_SENT,
                  shipmentCreated: true,
                },
              );
            } catch (leadError) {
              console.error("Error updating lead status:", leadError);
              // Don't fail the entire operation if lead update fails
            }

            // STOCK API
            operationResults.stock = { attempted: true, success: false, error: null };
            try {
              const processToken = await getFirebaseIDToken();

              // Create order payload with farmorderId explicitly set
              const orderPayload = {
                ...orderObj,
                farmorderId,
                id: fullDocId,
              };

              const stockResponse = await axios.post(urls.stockApiUrl, orderPayload, {
                headers: { Authorization: processToken },
              });

              const stockData = stockResponse.data;
              if (stockData && typeof stockData.success === "boolean") {
                operationResults.stock.success = stockData.success;
                if (!stockData.success) {
                  operationResults.stock.error = stockData.message || "Stock update returned an error";
                }
              } else {
                operationResults.stock.success = true;
              }
            } catch (stockError) {
              console.error("❌ Stock API error:", stockError);
              operationResults.stock.error = stockError.response?.data?.message || stockError.message;
            }

            // SOOCOOL API (if applicable)
            const sooCoolLabels = ["SCH", "SCM", "SCO"];
            if (sooCoolLabels.includes(deliveryLabel)) {
              operationResults.sooCool = { attempted: true, success: false, error: null };
              try {
                const processToken = await getFirebaseIDToken();

                // Create order payload with farmorderId explicitly set
                const orderPayload = {
                  ...orderObj,
                  farmorderId,
                  id: fullDocId,
                };

                const sooCoolResponse = await axios.post(urls.sooCoolOrder, orderPayload, {
                  headers: { Authorization: processToken },
                });
                operationResults.sooCool.success = true;
              } catch (sooCoolError) {
                console.error("❌ SooCool API error:", sooCoolError);
                operationResults.sooCool.error = sooCoolError.response?.data?.message || sooCoolError.message;
              }
            }
          } catch (firestoreError) {
            console.error("Firestore error:", firestoreError);
            operationResults.firestore.error = firestoreError.message;
            setLoading(false);
            showOperationSummary(operationResults);
            return;
          }

          showOperationSummary(operationResults);
          resetState();
          onClose();
        } catch (error) {
          console.error("Error creating shipment:", error);
          message.error(t("notifications.error_creating_shipment") + ": " + error.message);
        } finally {
          setLoading(false);
        }
      })
      .catch((error) => {
        // AntD throws a validation error object on failed validation
        setLoading(false);
      });
  };

  return (
    <Modal
      title={t("create_shipment")}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          {t("cancel")}
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={() => handleSubmit(false)}>
          {t("save")}
        </Button>,
      ]}
      width={600}
      centered
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col lg={24}>
            <Form.Item name="leadId" label={t("lead")} rules={[{ required: true, message: t("form.select_lead") }]}>
              <Select
                showSearch
                optionFilterProp="children"
                allowClear
                placeholder={t("form.choose_lead")}
                onChange={handleLeadChange}
              >
                {leads &&
                  [...leads]
                    .sort((a, b) => {
                      if (!a.companyName || !b.companyName) return 0;
                      return a.companyName.localeCompare(b.companyName);
                    })
                    .map((lead) => (
                      <Option key={lead.leadId} value={lead.leadId}>
                        {lead.companyName}
                      </Option>
                    ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ marginTop: 0 }} />

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryDate"
              label={t("delivery_date")}
              rules={[{ required: true, message: t("form.pick_value") }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                onChange={(date) => {
                  validateAndUpdateDate(date);
                  setSelectedDeliveryDateForStock(date);
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryLabel"
              label={t("delivery_label")}
              rules={[{ required: true, message: t("form.pick_value") }]}
            >
              <Select
                onChange={(value) => {
                  setDeliveryLabel(value);
                  // Auto-set first time slot when label changes
                  const deliverySettings = getCountryDelivery(selectedLead?.country || COMPANY_COUNTRY_CODE);
                  const labelConfig = deliverySettings?.deliveryLabels?.find((dl) => dl.deliveryLabel === value);
                  if (labelConfig?.deliveryTimeSlots?.length > 0) {
                    const firstSlot = labelConfig.deliveryTimeSlots[0];
                    form.setFieldValue("deliveryTimeSlot", stringifyTimeSlot(firstSlot));
                    setDeliveryTimeSlot(firstSlot);
                  }
                }}
                value={deliveryLabel}
                disabled={!selectedLead}
              >
                {getCountryDeliveryLabels(selectedLead?.country).map((d) => (
                  <Select.Option value={d.code} key={d.code}>
                    {d.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryTimeSlot"
              label={t("delivery_time_slot")}
              rules={[{ required: true, message: t("form.select_delivery_time_slot") }]}
            >
              <Select
                placeholder={t("select_delivery_time_slot")}
                disabled={!deliveryLabel || !selectedLead}
                onChange={(value) => {
                  try {
                    const slot = JSON.parse(value);
                    setDeliveryTimeSlot(slot);
                  } catch (e) {
                    console.error("Error parsing time slot:", e);
                  }
                }}
                optionLabelProp="label"
              >
                {deliveryLabel &&
                  selectedLead &&
                  (() => {
                    const deliverySettings = getCountryDelivery(selectedLead.country || COMPANY_COUNTRY_CODE);
                    return deliverySettings?.deliveryLabels
                      ?.find((dl) => dl.deliveryLabel === deliveryLabel)
                      ?.deliveryTimeSlots?.map((slot, index) => {
                        const label = `${slot.start} - ${slot.end}${
                          slot.deliverySurcharge > 0 ? ` (+€${slot.deliverySurcharge})` : ""
                        }`;
                        return (
                          <Select.Option key={index} value={stringifyTimeSlot(slot)} label={label}>
                            {label}
                          </Select.Option>
                        );
                      });
                  })()}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <h3>{t("products")}</h3>
        <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch checked={showAllProducts} onChange={setShowAllProducts} size="small" />
          <span style={{ fontSize: "14px" }}>{t("show_all_products")}</span>
        </div>

        {newOrderProducts?.map((product) => {
          const { productId, productName, itemCount } = product;
          const isExceedingStock = productsExceedingStock[productId];

          return (
            <EditOrderCartItem
              key={productId}
              style={
                isExceedingStock
                  ? {
                      borderLeft: "3px solid #ff4d4f",
                      paddingLeft: "8px",
                      backgroundColor: "rgba(255, 77, 79, 0.05)",
                    }
                  : {}
              }
            >
              <div style={{ display: "flex" }}>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const existingProductIndex = newOrderProducts.findIndex((item) => item.productId === productId);
                    if (existingProductIndex > -1) {
                      const newProducts = [...newOrderProducts];
                      newProducts.splice(existingProductIndex, 1);
                      setNewOrderProducts(newProducts);
                      setProductsExceedingStock((prev) => {
                        const newState = { ...prev };
                        delete newState[productId];
                        return newState;
                      });
                    }
                  }}
                  style={{ marginRight: "5px" }}
                />
                <div>
                  <h4>{productName}</h4>
                  {isExceedingStock && (
                    <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                      {t("exceeds_available_stock")}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <QuantityControl
                  productId={productId}
                  currentQuantity={itemCount}
                  onIncrease={() => increaseQuantity(product)}
                  onDecrease={() => decreaseQuantity(product)}
                  onManualInput={(productId, value) => {
                    const existingProductIndex = newOrderProducts.findIndex((item) => item.productId === productId);
                    const deliveryDate = form.getFieldValue("deliveryDate");
                    const packingDateId = getShipmentPackingDateId(deliveryDate);
                    const stockCarriedOver = stockCarryOverData[packingDateId] === true;
                    const stockInfo = stockData[packingDateId]?.[productId] || {
                      currentStock: 0,
                      projectedStock: 0,
                      requestedStock: 0,
                    };

                    if (value === 0) {
                      if (existingProductIndex > -1) {
                        const newProducts = [...newOrderProducts];
                        newProducts.splice(existingProductIndex, 1);
                        setNewOrderProducts(newProducts);
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    } else if (existingProductIndex > -1) {
                      const newProducts = [...newOrderProducts];
                      newProducts[existingProductIndex].itemCount = value;
                      setNewOrderProducts(newProducts);

                      let availableStock;
                      if (stockCarriedOver) {
                        availableStock = stockInfo.currentStock;
                      } else {
                        availableStock = (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
                      }

                      if (value > availableStock) {
                        setProductsExceedingStock((prev) => ({
                          ...prev,
                          [productId]: true,
                        }));
                      } else {
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    }
                  }}
                />
              </div>
            </EditOrderCartItem>
          );
        })}

        <NewProductsWrapper>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Select
              style={{ width: "100%" }}
              placeholder={t("add_products")}
              showSearch
              filterOption={(input, option) => {
                const productData = productDropdownOptions.find((p) => p.productId === option.value);
                return productData?.productName?.toLowerCase().includes(input.toLowerCase());
              }}
              onChange={(value) => {
                if (value) {
                  const product = productsToUse?.find((item) => item.productId === value);
                  if (!product) return;

                  const existsInCart = newOrderProducts.some((p) => p.productId === product.productId);

                  if (existsInCart) {
                    increaseQuantity(product);
                  } else {
                    addProductToNewOrder(product);
                  }
                  setNewProduct(null);
                }
              }}
              value={newProduct}
            >
              {productDropdownOptions.map((productData) => (
                <Select.Option key={productData.productId} value={productData.productId}>
                  {productData.productName}
                  {productData.isInStock ? (
                    ` (${t("stock")}: ${productData.availableStock})`
                  ) : (
                    <span style={{ color: "#ff4d4f" }}>{` (${t("stock")}: ${productData.availableStock})`}</span>
                  )}
                </Select.Option>
              ))}
            </Select>

            <Button onClick={openDefaultProductsModal}>{t("default_products")}</Button>
          </div>
        </NewProductsWrapper>
      </Form>

      <Modal
        title={t("configure_default_products")}
        open={defaultProductsModalOpen}
        onCancel={() => setDefaultProductsModalOpen(false)}
        zIndex={1200}
        okText={t("save")}
        cancelText={t("cancel")}
        onOk={async () => {
          const cleaned = (defaultProductsDraft || [])
            .filter((item) => item?.productId)
            .map((item) => ({
              productId: item.productId,
              quantity: Math.max(1, Math.floor(Number(item.quantity || 1))),
            }));

          await saveDefaultShipmentProducts(cleaned);
          setDefaultProductsModalOpen(false);

          if (!order) {
            applyDefaultsToCurrentShipment(cleaned);
          }
        }}
      >
        <div style={{ marginBottom: "12px" }}>{t("default_products_description")}</div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          <Select
            style={{ flex: 1 }}
            placeholder={t("add_default_product")}
            showSearch
            filterOption={(input, option) => {
              const productData = defaultProductsDropdownOptions.find((p) => p.productId === option.value);
              return productData?.productName?.toLowerCase().includes(input.toLowerCase());
            }}
            onChange={(value) => {
              if (!value) return;
              const product = productsToUse?.find((item) => item.productId === value);
              if (!product) return;

              const alreadyExists = defaultProductsDraft.some((p) => p.productId === product.productId);
              if (alreadyExists) {
                setNewDefaultProduct(null);
                return;
              }

              setDefaultProductsDraft((prev) => [
                ...prev,
                {
                  productId: product.productId,
                  productName: product.productName,
                  quantity: 1,
                },
              ]);
              setNewDefaultProduct(null);
            }}
            value={newDefaultProduct}
          >
            {defaultProductsDropdownOptions.map((productData) => (
              <Select.Option key={productData.productId} value={productData.productId}>
                {productData.productName}
              </Select.Option>
            ))}
          </Select>
        </div>

        {defaultProductsDraft?.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {defaultProductsDraft.map((item) => (
              <div
                key={item.productId}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}
              >
                <div style={{ flex: 1 }}>{item.productName || item.productId}</div>
                <InputNumber
                  min={1}
                  value={item.quantity}
                  onChange={(value) => {
                    setDefaultProductsDraft((prev) =>
                      prev.map((p) => (p.productId === item.productId ? { ...p, quantity: Number(value || 1) } : p)),
                    );
                  }}
                />
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setDefaultProductsDraft((prev) => prev.filter((p) => p.productId !== item.productId));
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div>{t("no_default_products_set")}</div>
        )}

        <Divider />

        <Button
          danger
          onClick={async () => {
            await saveDefaultShipmentProducts([]);
            setDefaultProductsDraft([]);
            if (!order) {
              applyDefaultsToCurrentShipment([]);
            }
          }}
        >
          {t("clear_defaults")}
        </Button>
      </Modal>
    </Modal>
  );
};

const NewProductsWrapper = styled.div`
  .ant-collapse-content-box {
    padding-inline: 0 !important;
  }
`;

const EditOrderCartItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;

  h4 {
    margin: 0;
    font-size: 16px;
    line-height: 1.2;
  }

  p {
    margin: 0;
  }

  .quantity-chooser {
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px solid rgb(232, 232, 232);
    border-radius: 8px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1;
      padding-inline: 6px;
      min-width: 50px;
      text-align: center;
      color: #1a3353;
    }

    .anticon {
      font-size: 12px;
      padding: 8px 6px;
      cursor: pointer;
    }
  }
`;

export default AddShipmentModal;
